import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getHorizonIngestApps, getHorizonOptOutDetails } from '../api/horizonOptOutTracker';
import { ALL_HORIZON_REGIONS } from '../types/horizonOptOutTracker';
import type {
  HorizonOptOutDateRange,
  HorizonOptOutDisplayRow,
  HorizonRegionFilter,
} from '../types/horizonOptOutTracker';
import { getDefaultHorizonOptOutDateRange, getHorizonOptOutDateBounds } from '../utility/horizonOptOutTracker';

const horizonOptOutTrackerQueryKey = 'HorizonOptOutTracker';
const eventRequestBatchSize = 5;

export interface HorizonOptOutTrackerResult {
  data: HorizonOptOutDisplayRow[];
  isEnriching: boolean;
  isError: boolean;
  isLoading: boolean;
}

/** Loads disqualified Horizon apps first, then progressively enriches details from the selected date range. */
export const useHorizonOptOutTracker = (
  dateRange?: HorizonOptOutDateRange,
  region: HorizonRegionFilter = ALL_HORIZON_REGIONS,
): HorizonOptOutTrackerResult => {
  const [rows, setRows] = useState<HorizonOptOutDisplayRow[]>([]);
  const [rowsFilterKey, setRowsFilterKey] = useState<string>();
  const [isEnriching, setIsEnriching] = useState(false);
  const [defaultDateRange] = useState(getDefaultHorizonOptOutDateRange);
  const { endDate, startDate } = dateRange ?? defaultDateRange;
  const { windowEnd, windowStart } = getHorizonOptOutDateBounds({ endDate, startDate });
  const filterKey = `${windowStart}|${windowEnd}|${region}`;
  const ingestAppsQuery = useQuery({
    queryKey: [horizonOptOutTrackerQueryKey, windowStart, windowEnd, region],
    queryFn: () => getHorizonIngestApps(windowStart, windowEnd, region),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!ingestAppsQuery.data) return;

    let isCancelled = false;
    const abortController = new AbortController();
    const ingestApps = ingestAppsQuery.data;
    setRows(ingestApps.map((ingestApp) => ({ ...ingestApp, detailStatus: 'loading' })));
    setRowsFilterKey(filterKey);
    setIsEnriching(ingestApps.length > 0);

    const enrichRows = async () => {
      for (let index = 0; index < ingestApps.length; index += eventRequestBatchSize) {
        const batch = ingestApps.slice(index, index + eventRequestBatchSize);
        const results = await Promise.allSettled(
          batch.map((ingestApp) => getHorizonOptOutDetails(ingestApp, windowStart, windowEnd, abortController.signal)),
        );
        if (isCancelled) return;

        const updates = new Map<string, HorizonOptOutDisplayRow | null>(
          results.map((result, resultIndex): [string, HorizonOptOutDisplayRow | null] => {
            const ingestApp = batch[resultIndex];
            if (result.status === 'rejected') {
              return [ingestApp.appUid, { ...ingestApp, detailStatus: 'error' as const }];
            }
            if (!result.value) return [ingestApp.appUid, null];
            return [
              ingestApp.appUid,
              {
                ...ingestApp,
                detailStatus: 'loaded' as const,
                optOutReason: result.value.optOutReason,
                optOutTimeUTC: result.value.optOutTimeUTC,
                type: result.value.type,
              },
            ];
          }),
        );
        setRows((currentRows) =>
          currentRows.flatMap((row) => {
            const updatedRow = updates.get(row.appUid);
            return updatedRow === null ? [] : [updatedRow ?? row];
          }),
        );
      }

      if (!isCancelled) setIsEnriching(false);
    };

    void enrichRows();
    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [filterKey, ingestAppsQuery.data, windowEnd, windowStart]);

  const visibleRows = rowsFilterKey === filterKey ? rows : [];
  const isVisibleRangeEnriching = rowsFilterKey === filterKey && isEnriching;

  return {
    data: visibleRows,
    isEnriching: isVisibleRangeEnriching,
    isError: ingestAppsQuery.isError && visibleRows.length === 0,
    isLoading:
      ingestAppsQuery.isLoading ||
      (ingestAppsQuery.data !== undefined && ingestAppsQuery.data.length > 0 && rowsFilterKey !== filterKey),
  };
};
